import { isDefined, toError } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { useDeleteShoppableQualification } from "../../api/useDeleteShoppableQualification";
import { ROLE_REMOVED_MODAL_PATH } from "../../paths";
import { type RemoveRoleModalRouteParams } from "../../types";
import { useRoleDetailsData } from "../useRoleDetailsData";
import { RemoveRoleBottomSheet } from "./RemoveBottomSheet";

interface RemoveRoleModalContainerProps {
  modalState: UseModalState;
  rolesPath: string;
}

export function RemoveRoleModalContainer(props: RemoveRoleModalContainerProps) {
  const { modalState, rolesPath } = props;

  const history = useHistory();
  const { userId: workerId, shoppableQualifications } = useDefinedWorker();

  const { shoppableQualificationId } = useParams<RemoveRoleModalRouteParams>();

  const { mutateAsync: deleteShoppableQualification, isLoading: isRemovingRole } =
    useDeleteShoppableQualification();

  const { showErrorToast } = useToast();

  const shoppableQualification = shoppableQualifications.find(
    (shoppableQualification) => shoppableQualification.id === shoppableQualificationId
  );
  const qualificationId = shoppableQualification?.relationships.qualification.data.id;

  const { qualificationLabel } = useRoleDetailsData(qualificationId ?? "");

  return (
    <RemoveRoleBottomSheet
      modalState={modalState}
      isRemovingRole={isRemovingRole}
      roleName={qualificationLabel}
      onRemoveRole={async () => {
        try {
          if (!isDefined(qualificationId)) {
            throw new Error(
              `Missing qualification id for shoppable qualification ${shoppableQualificationId}`
            );
          }

          await deleteShoppableQualification({
            shoppableQualificationId,
            workerId,
          });

          logEvent(APP_V2_USER_EVENTS.ROLE_REMOVED, {
            qualificationId,
            workerId,
          });

          history.replace(
            generatePath(`${rolesPath}/${ROLE_REMOVED_MODAL_PATH}`, {
              qualificationId,
            })
          );
        } catch (maybeError) {
          const error = toError(maybeError);

          showErrorToast("Something went wrong while removing the role. Please try again.");

          logError(APP_V2_APP_EVENTS.REMOVE_ROLE_FAILURE, {
            metadata: { qualificationId, workerId },
            error,
          });
        }
      }}
    />
  );
}
